<template>
  <div>
    <div v-for="(button, idx) in zoomViewButtons" :key="`zoomView-${idx}`">
      <base-button v-bind="button.props" @click="zoomView(button.value)" />
    </div>
    <div class="tw:bg-white tw:mt-1 tw:rounded-md tw:border tw:border-gray-200 tw:shadow-md">
      <div
        v-for="(button, idx) in toolbarButtons"
        :key="`toolbar-${idx}`"
        v-show="!toolbarHidden || activeTool === button.name"
        class="tw:relative tw:hover:[&_.hoverable-items]:inline-block"
      >
        <template v-if="button.items">
          <template v-if="getButtonCurrentItem(button).menu">
            <v-menu
              :value="activeTool === getButtonCurrentItem(button).name"
              v-bind="getButtonCurrentItem(button).menu"
            >
              <template #activator="{}">
                <base-button
                  v-bind="getButtonCurrentItem(button).props"
                  :toggled="activeTool === getButtonCurrentItem(button).name"
                  @click="onToolbarButton(getButtonCurrentItem(button))"
                />
              </template>
              <template v-if="getButtonCurrentItem(button).name === 'toolbar-XY'">
                <v-card class="tw:px-3 tw:py-3 tw:w-[300px] tw:ml-auto tw:shadow-md">
                  <v-row no-gutters align="center" class="flex-nowrap">
                    <v-col cols="auto" class="pr-1 flex-shrink-1">
                      <data-input
                        v-model="xyCoords[0]"
                        v-bind="xyCoordBind"
                        label="X"
                        class="tw:smaller-input"
                        @keydown.enter="onXy(xyCoords)"
                      />
                    </v-col>
                    <v-col cols="auto" class="pl-1 flex-shrink-1">
                      <data-input
                        v-model="xyCoords[1]"
                        v-bind="xyCoordBind"
                        label="Y"
                        class="tw:smaller-input"
                        @keydown.enter="onXy(xyCoords)"
                      />
                    </v-col>
                    <v-col cols="auto" class="flex-grow-1 pl-3">
                      <v-icon color="iconIdle" size="18" @click="onXy(xyCoords)" :disabled="!xyCoordsValid">
                        mdi-arrow-left-bottom
                      </v-icon>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-menu>
          </template>
          <base-button
            v-else
            v-bind="getButtonCurrentItem(button).props"
            :toggled="activeTool === getButtonCurrentItem(button).name"
            @click="onToolbarButton(getButtonCurrentItem(button))"
          />
          <div class="tw:absolute! tw:left-[36px] tw:top-0 tw:z-1 hoverable-items tw:hidden">
            <v-row no-gutters class="flex-nowrap">
              <v-col cols="auto" v-for="(item, itemIdx) in getButtonRestItems(button)" :key="itemIdx">
                <base-button
                  v-bind="{
                    ...item.props,
                    ...(itemIdx === getButtonRestItems(button).length - 1
                      ? { class: 'tw:border-0 tw:rounded-l-none tw:rounded-r-xl!' }
                      : {}),
                  }"
                  :toggled="activeTool === item.name"
                  @click="onToolbarButton(item)"
                />
              </v-col>
            </v-row>
          </div>
        </template>
        <base-button
          v-else
          v-bind="button.props"
          :toggled="activeTool === button.name"
          @click="onToolbarButton(button)"
        />
      </div>
      <div v-show="!toolbarHidden || activeTool === selectionButton.name">
        <base-button
          v-bind="selectionButton.props"
          :toggled="activeTool === selectionButton.name"
          @click="toggleSelection()"
        />
      </div>
      <div class="tw:relative" :class="{ 'map-toolbar-options-border': !toolbarHidden }" />
      <div v-if="toolbarGeometriesType" v-show="!toolbarHidden">
        <dots-menu :items="optionsButton.items" @save="onToolbarSave()" @advancedAreaSearch="onAdvancedAreaSearch()">
          <base-button v-bind="optionsButton.props" />
        </dots-menu>
      </div>
      <div v-if="toolbarGeometriesType" v-show="!toolbarHidden">
        <base-button v-bind="clearButton.props" @click="onToolbarClear()" />
      </div>
      <div>
        <div class="tw:bg-white tw:hover:bg-gray-50 tw:cursor-pointer tw:relative tw:w-9" @click="toggleFold()">
          <v-icon color="font" size="18">
            {{ toolbarHidden ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
          </v-icon>
        </div>
      </div>
    </div>
    <main-dialog
      v-if="activeToolConfirmationVisible !== null"
      :is-visible.sync="activeToolConfirmationVisible"
      :title="$i18n.t('map.toolbar.activeToolChange')"
      :body="$i18n.t('map.toolbar.activeToolChangeConfirmation')"
      @agree="activeToolConfirmationAgree"
      agree-text="dialog.agreeChangeTool"
    />
    <data-save-dialog
      v-if="toolbarSaveVisible !== null"
      :is-visible.sync="toolbarSaveVisible"
      :geojson="toolbarGeometries?.geojson"
      :type="toolbarGeometriesType"
    />
    <the-navbar-tools-panel-usable-area ref="usable-area" v-if="hasUsableArea" v-bind="usableAreaBind" />
  </div>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import TheNavbarToolsPanelUsableArea from '@/components/TheNavbarToolsPanelUsableArea';

import { getContext } from '@/assets/js/mapContext';

import mapToolbar from '@/mixins/mapToolbar';

export default {
  name: 'MapToolbar',
  components: {
    DataInput,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    DataSaveDialog: () => import('@/components/DataSaveDialog'),
    TheNavbarToolsPanelUsableArea,
  },
  inject: {
    mapContext: {
      default: null,
    },
  },
  mixins: [mapToolbar],
  computed: {
    activeTool: sync('tools/activeTool'),
    reports: get('locationLab/reports'),
    toolbarButtons() {
      const defaultProps = {
        type: 'map-control',
        tooltipProps: { right: true },
      };
      const middleClasses = 'tw:border-0 tw:rounded-b-none tw:rounded-t-none';

      return [
        {
          name: 'toolbar-point',
          items: [
            {
              name: 'toolbar-Point',
              props: {
                ...defaultProps,
                tooltip: this.$i18n.t('map.toolbar.drawPoint'),
                icon: 'mdi-vector-point',
                class: 'tw:border-0 tw:rounded-b-none',
              },
            },
            {
              name: 'toolbar-XY',
              supGeometry: 'Point',
              menu: { closeOnClick: false, closeOnContentClick: false, right: true, nudgeRight: 38, nudgeTop: 10 },
              props: {
                ...defaultProps,
                tooltip: this.$i18n.t('map.toolbar.drawXY'),
                icon: '$coordinates',
                class: 'tw:border-0 tw:rounded-b-none',
              },
            },
          ],
        },
        {
          name: 'toolbar-LineString',
          props: {
            ...defaultProps,
            tooltip: this.$i18n.t('map.toolbar.drawLine'),
            icon: 'mdi-vector-line',
            class: middleClasses,
          },
        },
        {
          name: 'toolbar-area',
          items: [
            {
              name: 'toolbar-Polygon',
              props: {
                ...defaultProps,
                tooltip: this.$i18n.t('map.toolbar.drawPolygon'),
                icon: 'mdi-vector-square',
                class: middleClasses,
              },
            },
            {
              name: 'toolbar-Circle',
              supGeometry: 'Polygon',
              props: {
                ...defaultProps,
                tooltip: this.$i18n.t('map.toolbar.drawCircle'),
                icon: 'mdi-vector-circle',
                class: middleClasses,
              },
            },
          ],
        },
      ];
    },
    optionsButtonPassTools() {
      return [
        ...(this.hasAdvancedAreaSearch
          ? [
              {
                name: 'advancedAreaSearch',
                customName: this.$i18n.t('navbar.databoxAdvancedAreaSearch'),
                customAction: () => this.setActiveTool('advancedAreaSearch'),
              },
            ]
          : []),
        ...(this.hasDataboxDataV2
          ? [
              {
                name: 'databoxData2',
                customName: this.$i18n.t('admin.databoxDataDownload'),
                customAction: () => this.setActiveTool('databoxData2'),
              },
            ]
          : []),
        ...(this.hasUsableArea
          ? [
              {
                name: 'usableArea',
                customName: this.$store.get('admin/additionalModules')?.OZE_USABLE_AREA_MODULE?.verbose_name,
                customAction: () => this.setActiveTool('usableArea', 'usable-area'),
              },
            ]
          : []),
      ];
    },
    optionsButton() {
      return {
        items: [
          ...(this.hasReports
            ? [
                {
                  name: 'reports',
                  customName: this.$i18n.t('map.toolbar.saveDialog.reports'),
                  customIcon: 'mdi-clipboard-text',
                  customItems: this.reports.map(report => {
                    return { name: report.id, customName: report.name, customAction: () => this.onReport(report.id) };
                  }),
                },
              ]
            : []),
          ...(this.optionsButtonPassTools.length > 0
            ? [
                {
                  name: 'passToTool',
                  customIcon: 'mdi-arrow-right',
                  customItems: this.optionsButtonPassTools,
                },
              ]
            : []),
          ...(this.toolbarGeometriesType !== 'mixed'
            ? [
                {
                  name: 'save',
                  customIcon: 'mdi-content-save',
                  customName: this.$i18n.t('map.toolbar.saveDialog.save'),
                },
              ]
            : []),
        ],
        props: {
          type: 'map-control',
          tooltipProps: { right: true },
          icon: 'mdi-dots-vertical',
          class: 'tw:border-0 tw:rounded-b-none tw:rounded-t-none',
        },
      };
    },
    selectionButton() {
      return {
        name: 'identification-toolbarSelection',
        props: {
          type: 'map-control',
          tooltipProps: { right: true },
          tooltip: this.$i18n.t('map.toolbar.selectFeature'),
          icon: 'mdi-cursor-default-outline',
          class: 'tw:border-0 tw:rounded-b-none tw:rounded-t-none',
        },
      };
    },
    clearButton() {
      return {
        name: 'toolbar-clear',
        props: {
          type: 'map-control',
          tooltipProps: { right: true },
          tooltip: this.$i18n.t('menu.clearAll'),
          icon: 'mdi-delete-outline',
          class: 'tw:border-0 tw:rounded-b-none tw:rounded-t-none',
        },
      };
    },
    xyCoordBind() {
      return { dataType: { name: 'float' }, hideDetails: true, dense: true };
    },
    xyCoordsValid() {
      return this.xyCoords.every(coord => !!coord || coord === 0);
    },
    usableAreaBind() {
      return {
        name: 'usableArea',
        verboseName: this.$store.get('admin/additionalModules')?.OZE_USABLE_AREA_MODULE?.verbose_name,
      };
    },
    zoomViewButtons() {
      const defaultProps = {
        type: 'map-control',
        tooltipProps: { right: true },
      };
      return [
        {
          value: true,
          props: {
            ...defaultProps,
            tooltip: this.$i18n.t('navbar.zoomIn'),
            icon: 'mdi-plus',
            class: 'tw:shadow-md',
          },
        },
        {
          value: false,
          props: {
            ...defaultProps,
            tooltip: this.$i18n.t('navbar.zoomOut'),
            icon: 'mdi-minus',
            class: 'tw:mt-1 tw:shadow-md',
          },
        },
      ];
    },
    isPermitted: get('admin/isPermitted'),
    hasReports() {
      return this.isPermitted({ module: 'LOCATION_LAB', setting: 'location_lab_module_enabled' });
    },
    hasAdvancedAreaSearch() {
      return (
        this.isPermitted({ module: 'DATABOX_DATA_MODULE', setting: 'databox_advanced_area_search' }) &&
        (this.toolbarGeometriesType.includes('Polygon') || this.toolbarGeometriesType === 'Circle') &&
        this.toolbarGeometriesLength === 1
      );
    },
    hasDataboxDataV2() {
      return (
        this.isPermitted({ module: 'DATABOX_DATA_MODULE', setting: 'databox_data_download_v2' }) &&
        (this.toolbarGeometriesType.includes('Polygon') || this.toolbarGeometriesType === 'Circle') &&
        this.toolbarGeometriesLength === 1
      );
    },
    hasUsableArea() {
      return (
        this.isPermitted({ module: 'OZE_USABLE_AREA_MODULE' }) &&
        (this.toolbarGeometriesType.includes('Polygon') || this.toolbarGeometriesType === 'Circle') &&
        this.toolbarGeometriesLength >= 1
      );
    },
  },
  data: () => ({
    activeToolConfirmationData: { oV: null, nV: null },
    activeToolConfirmationVisible: null,
    buttonsCurrentItem: {},
    toolbarHidden: false,
    toolbarSaveVisible: null,
    xyCoords: [],
  }),
  methods: {
    getReports: call('locationLab/getLocationLabReports'),
    activeToolConfirmationAgree() {
      const { oV, nV } = this.activeToolConfirmationData;
      this.activateTool(nV, oV);
      this.activeToolConfirmationVisible = false;
    },
    onReport(rid = null) {
      this.$router.push({ name: 'locationLabReport', params: { rid } });
    },
    onXy(coords = this.xyCoords) {
      if (!this.xyCoordsValid) return;
      getContext(this).$emit('toolbar-xy', { coords });
      // this.xyCoords = [null, null];
    },
    onToolbarButton({ name: nV, supGeometry = null }) {
      const oV =
        this.activeTool?.startsWith('toolbar-') || this.activeTool === this.selectionButton.name
          ? this.activeTool
          : null;
      const ooV = this.toolbarGeometriesType ? `toolbar-${this.toolbarGeometriesType}` : null;
      if (oV === nV && !ooV) this.onToolbarClear();
      else if (oV === nV) this.activateTool(null, oV);
      else if (ooV && (ooV === nV || ooV === `toolbar-${supGeometry}`)) this.activateTool(nV, ooV);
      else if (!oV && !ooV) this.activateTool(nV, oV);
      else if (!ooV && oV && nV && oV !== nV) this.activateTool(nV, oV);
      else {
        this.activeToolConfirmationData = { oV: oV || ooV, nV };
        this.activeToolConfirmationVisible = true;
      }
    },
    onToolbarClear() {
      getContext(this).$emit('toolbar-toggle', { type: null });
      getContext(this).$emit('toolbar-selection', { value: false });
    },
    onToolbarSave() {
      this.toolbarSaveVisible = true;
    },
    setActiveTool(name, ref) {
      this.$store.commit('navbarMenus/SET_ACTIVE_TOOL', {
        data: { name, outerParams: { toolbarInitGeometry: this.toolbarGeometries.geojson } },
        value: true,
      });
      if (this.$refs[ref]) this.$refs[ref].isMenuOpen = true;
    },
    zoomView(value) {
      getContext(this).$emit(value ? 'zoomIn-action' : 'zoomOut-action');
    },
    toggleFold(value = !this.toolbarHidden) {
      this.toolbarHidden = value;
      localStorage.setItem('mapToolbarHidden', value);
    },
    toggleSelection(value = this.activeTool !== this.selectionButton.name) {
      getContext(this).$emit('toolbar-selection', { value });
    },

    activateTool(nV = null, oV = null) {
      const mainToolbarButton = this.toolbarButtons.find(b => b.items?.some(i => i.name === nV));
      if (mainToolbarButton) {
        this.buttonsCurrentItem[mainToolbarButton.name] = nV;
        localStorage.setItem(`buttonsCurrentItem`, JSON.stringify(this.buttonsCurrentItem));
      }
      const toolbarButton =
        this.toolbarButtons.find(b => b.name === nV) ||
        this.$_flattenStructure(this.toolbarButtons, 'items').find(b => b.name === nV);
      if (nV && !oV) getContext(this).$emit('toolbar-toggle', { type: nV.split('-')[1] });
      else if (!nV && oV)
        getContext(this).$emit('toolbar-interactions', { names: ['draw', 'translate', 'modify', 'snap'], type: null });
      else if (nV && oV) {
        if (nV === oV || toolbarButton.supGeometry === oV.split('-')[1])
          getContext(this).$emit('toolbar-interactions', {
            names: ['draw', 'translate', 'modify', 'snap'],
            type: nV.split('-')[1],
          });
        else {
          getContext(this).$emit('toolbar-toggle', { type: null });
          this.$nextTick(() => {
            getContext(this).$emit('toolbar-toggle', { type: nV.split('-')[1] });
          });
        }
      }
    },
    getButtonCurrentItem(button) {
      if (!button.items?.length > 1) return button;
      return button.items.find(item => item.name === this.buttonsCurrentItem[button.name]) || button.items[0];
    },
    getButtonRestItems(button) {
      if (!button.items?.length > 1) return [];
      const currentItem =
        button.items.find(item => item.name === this.buttonsCurrentItem[button.name]) || button.items[0];
      return button.items.filter(item => item.name !== currentItem.name);
    },

    async init() {
      await this.getReports();
    },
  },
  mounted() {
    this.toolbarHidden = localStorage.getItem('mapToolbarHidden') === 'true';
    this.buttonsCurrentItem = JSON.parse(localStorage.getItem(`buttonsCurrentItem`) || '{}');
    this.init();
  },
};
</script>
<style scoped>
@reference '@/tailwind.css';
.map-toolbar-options-border::after {
  @apply tw:content-[''] tw:absolute tw:top-0 tw:left-0 tw:w-[calc(100%-16px)] tw:h-[1px] tw:bg-gray-200 tw:mx-2 tw:z-1;
}
</style>
